/**
 * USDA WWEIA Food Group Classifications
 *
 * Types inferred from JSON data. Codes are literal types, names are strings.
 *
 * @see https://www.ars.usda.gov/northeast-area/beltsville-md-bhnrc/beltsville-human-nutrition-research-center/food-surveys-research-group/docs/wweia-documentation-and-datasets/
 */

import foodGroupsJson from "./data/food-groups.json";

// Literal type union inferred from JSON
type RawFoodGroup = (typeof foodGroupsJson.groups)[number];
export type FoodGroupCode = RawFoodGroup["code"];

// Runtime interface with widened name
export interface FoodGroupDefinition {
  code: FoodGroupCode;
  name: string;
}

export const FOOD_GROUPS: FoodGroupDefinition[] = foodGroupsJson.groups;

const FOOD_GROUP_BY_CODE = FOOD_GROUPS.reduce(
  (acc, group) => {
    acc[group.code] = group;
    return acc;
  },
  {} as Record<FoodGroupCode, FoodGroupDefinition>
);

export function getFoodGroupLabel(code: string): string {
  if (!isFoodGroupCode(code)) {
    return "Unknown";
  }
  return FOOD_GROUP_BY_CODE[code]?.name ?? "Unknown";
}

export function isFoodGroupCode(value: string): value is FoodGroupCode {
  return value in FOOD_GROUP_BY_CODE;
}

export function toFoodGroupCode(value: string): FoodGroupCode {
  if (!isFoodGroupCode(value)) {
    throw new Error(`Invalid food group code: ${value}`);
  }
  return value;
}

export function getFoodGroupDefinition(
  code: FoodGroupCode
): FoodGroupDefinition | undefined {
  return FOOD_GROUP_BY_CODE[code];
}
